/**
 * THE WORK THAT IS NOT A PRODUCT, ON THE PROPOSAL.
 *
 * Jacky, 18 September, on the real O-00000005 draft — three sentences that between them decide this
 * whole file:
 *
 *   *"I am missing optional work for all the remove & dispose, and there is also remove & not dispose
 *   (customer provide skip) assumption dispose is 50% of the fee. this should be registered somewhere as
 *   Artemis service cost. and proposal; read the config. I can always add more to config. or add a custom
 *   service where I define QTY, UOM, Rate per."*
 *
 *   *"any additional work I add in, I can tick entire item is optional, typical with skirting, I offer 2
 *   sub option. same as Remove & Dispose 2 sub option."*
 *
 *   *"and I also add item that is subject to variation, price is set, not added to pricing unless used,
 *   and customer will approve during installation when I need to use it."*
 *
 * So a service line is one of three things, and the difference is what the CUSTOMER may do about it:
 *
 *   · **included** — it is in the price, priced from the Artemis service cost config.
 *   · **optional** — offered, with its sub-options (his typical shape is two), and NOT in the total
 *     until the customer picks one. Declining is allowed.
 *   · **variation** — priced and shown, charged only if it is used, approved on site on the day.
 *
 * It also carries where the quantity came from, because that is the part he does not want to type twice:
 * a floor area, a drawn skirting run, the stair steps, or his own number for a custom service.
 */
import { z } from "zod";
const money = z.number().finite().min(-1e9).max(1e9);
// Draft and customer pricing must accept the same work notes: a valid saved
// accessory/service must never crash the editor when projected for the customer.
export const PROPOSAL_WORK_NOTE_MAX_LENGTH = 500;
const text = z.string().trim().max(PROPOSAL_WORK_NOTE_MAX_LENGTH);
export const SkirtingHeightBand = z.enum(['standard', 'medium', 'tall']);
/**
 * A small, proposal-safe catalogue attached to one service option.
 *
 * It contains only what the customer may choose: shape and a practical nominal
 * height band. Brand, stock and purchasing alternatives stay in the service
 * configuration so Artemis may buy an equivalent board without asking the
 * customer to make a supplier decision.
 */
export const CustomerServiceCatalogue = z.object({
    id: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(160),
    note: z.string().trim().max(500).default(''),
    /** How the board is used, which decides how it is DRAWN: a skirting is drawn as a section on the
     *  floor; an architrave is the same section run up both sides of a door and across the head, mitred
     *  at 45° (Jacky, 30 September 2026). Older released catalogues are skirting. */
    application: z.enum(['skirting', 'architrave']).default('skirting'),
    items: z.array(z.object({
        id: z.string().trim().min(1).max(60),
        label: z.string().trim().min(1).max(120),
        description: z.string().trim().max(300).default(''),
        /** A closed visual vocabulary lets the web face draw a profile without accepting arbitrary SVG. */
        visual: z.enum(['bevelled', 'half_splayed', 'pencil_round', 'bullnose', 'colonial', 'lambs_tongue']),
        /** Repo-hosted product reference. Restricting the path prevents a released Proposal depending on a retailer URL. */
        representative_image_url: z.string().regex(/^\/images\/skirting\/[a-z0-9-]+\.jpg$/).max(160).nullable().default(null),
        /** Repo-hosted illustrative room view. Kept separate so it cannot be mistaken for the promised product. */
        installed_image_url: z.string().regex(/^\/images\/skirting\/installed\/[a-z0-9-]+\.jpg$/).max(180).nullable().default(null),
        variants: z.array(z.object({
            id: z.string().trim().min(1).max(80),
            label: z.string().trim().min(1).max(160),
            material: z.string().trim().min(1).max(120),
            height_min_mm: z.number().int().positive().max(500),
            height_max_mm: z.number().int().positive().max(500),
            thickness_mm: z.number().int().positive().max(100),
            /** Stable band shared by every decorative profile. Null preserves older released Proposals. */
            height_band: SkirtingHeightBand.nullable().default(null),
            /** Adjustment against the standard installed quote rate, never a replacement supply price. */
            price_adjustment_per_lm: money.nullable().default(null),
        }).refine((variant) => variant.height_max_mm >= variant.height_min_mm, { message: 'height band must be ordered' })).min(1).max(20),
    })).min(1).max(30),
});
export const CustomerServiceSelection = z.object({
    catalogue_id: z.string().trim().min(1).max(60),
    item_id: z.string().trim().min(1).max(60),
    variant_id: z.string().trim().min(1).max(80),
});
/** One of the two (or more) shapes a piece of optional work can take. */
export const ServiceOption = z.object({
    id: z.string().trim().min(1).max(60),
    label: z.string().trim().min(1).max(160),
    rate: money.nullable().default(null),
    note: text.default(''),
    /** Required customer selection after this service option is chosen, when present. */
    catalogue: CustomerServiceCatalogue.nullable().default(null),
    /** The height already included in this customer's quoted installed rate. */
    included_height_band: SkirtingHeightBand.nullable().default(null),
});
export const SERVICE_SOURCES = ['config', 'custom', 'canvas', 'product'];
/**
 * A service's unit (5 November 2026). It was a fixed list — m², LM, step, each, hour, doorway — and Jacky, pricing
 * Cate's levelling (J-1026-10308) at 30 bags × $120: "the quantity unit doesnt give me custom option, that line is per
 * bag". He chose "any typed unit". A service's unit is only ever PRINTED ("30 bags × $120"): no wastage, carpet, area or
 * doorway rule reads it (those read the flooring product's unit), so a typed unit cannot move a figure. Common spellings
 * still snap to the six known units, so "m2" or "sqm" is m² wherever m² matters.
 */
export const SERVICE_UNITS = ['m²', 'LM', 'step', 'each', 'hour', 'doorway'];
const UNIT_SPELLINGS = {
    'm²': 'm²', 'm2': 'm²', 'm^2': 'm²', 'sqm': 'm²', 'sq m': 'm²', 'sq.m': 'm²', 'sq. m': 'm²', 'square metre': 'm²', 'square metres': 'm²',
    'lm': 'LM', 'l/m': 'LM', 'lin m': 'LM', 'lineal metre': 'LM', 'lineal metres': 'LM', 'linear metre': 'LM', 'linear metres': 'LM',
    'step': 'step', 'steps': 'step', 'each': 'each', 'ea': 'each', 'ea.': 'each',
    'hour': 'hour', 'hours': 'hour', 'hr': 'hour', 'hrs': 'hour', 'doorway': 'doorway', 'doorways': 'doorway',
};
/** The unit as stored: a known spelling becomes its standard unit; anything else is kept as typed, spaces tidied. */
export function normaliseServiceUnit(unit) {
    const typed = unit.trim().replace(/\s+/g, ' ');
    return UNIT_SPELLINGS[typed.toLowerCase()] ?? typed;
}
export const ServiceUnit = z.string().trim().min(1).max(20).overwrite(normaliseServiceUnit);
export const ProposalServiceLine = z.object({
    id: z.string().trim().min(1).max(60),
    /** The `artemis_services` config entry this came from, when it came from one. */
    service_id: z.string().trim().max(60).nullable().default(null),
    /** Staff-only catalogue cost selection; removed from every customer copy. */
    execution_basis: z.enum(['artemis', 'contractor']).optional(),
    catalogue_option_id: z.string().trim().max(60).optional(),
    service_conditions: z.string().trim().max(500).optional(),
    skirting_room_exclusions: z.array(z.string().max(240)).max(200).optional(),
    /** A removal line's existing floor ("Carpet", "Laminate"…). Set by syncRemovalLines: the line's quantity is
     *  then the in-scope area of the rooms with that floor (proposal-removal.ts). Absent = the whole floor area. */
    removal_floor: z.string().trim().max(80).optional(),
    name: z.string().trim().min(1).max(160),
    unit: ServiceUnit,
    quantity_source: z.enum(['manual', 'assessment']).optional(),
    measured_quantity: z.number().finite().nonnegative().max(1e8).optional(),
    quantity: z.number().finite().nonnegative().max(1e8),
    /** Per unit, GST exclusive. `null` = not approved yet, and the line says so rather than guessing. */
    rate: money.nullable().default(null),
    // ── what the customer may do about it ──────────────────────────────────────
    /** Offered rather than quoted: it is a choice, and it is not in the total until one is chosen. */
    optional: z.boolean().default(false),
    /** Priced, shown, and charged only if it is used. Approved on site when it is. */
    variation: z.boolean().default(false),
    /** Tick off to leave it out entirely. An optional line ticked on is still a choice, not a purchase. */
    offered: z.boolean().default(true),
    /** Which sub-option is the one being offered as the default, if any. */
    chosen_option: z.string().trim().max(60).nullable().default(null),
    options: z.array(ServiceOption).max(4).default([]),
    note: text.default(''),
    /** Where the quantity came from — `canvas` means it was measured, not typed. */
    source: z.enum(SERVICE_SOURCES).default('config'),
});
/**
 * AN ACCESSORY, PER SITE OR PER FLOOR.
 *
 * Jacky: *"I need to able switch on accessories offered. such as whole site - Green underlay, or 1F
 * Green, GF Silver, if I set scotia on canvas then bring on ticked scotia"*, and *"remeber the
 * accessories that is binded to main product, customer will see indent line showing its included in the
 * price"*. So an accessory has a SCOPE (the whole site, or one drawn floor), a colour choice, a tick,
 * and — when it comes with the product — a binding that makes it part of that product's price.
 */
export const ProposalAccessory = z.object({
    id: z.string().trim().min(1).max(60),
    name: z.string().trim().min(1).max(160),
    scope: z.enum(['site', 'area']).default('site'),
    /** The drawn floor this belongs to when the scope is `area`. */
    area_id: z.string().trim().max(80).default(''),
    /** A trim row seeded from the drawing (16 Oct 2026): the drawn profile ("L Trim", "U Channel") whose drawn length is its
     *  quantity. Absent for a trim someone added by hand. */
    trim_profile: z.string().trim().max(80).optional(),
    colour: z.string().trim().max(160).nullable().default(null),
    /** Ticked on: offered or included, depending on `bound_to`. */
    on: z.boolean().default(false),
    /** The product it comes with. Set = shown indented as INCLUDED IN THE PRICE, with no figure. */
    bound_to: z.string().trim().max(80).nullable().default(null),
    unit: z.string().trim().min(1).max(20).default('m²'),
    quantity_source: z.enum(['manual', 'assessment']).optional(),
    measured_quantity: z.number().finite().nonnegative().max(1e8).optional(),
    /** THE ONE LINK (26 Oct 2026). The measured runs this row is made of — ids from the one reader (`assessmentAccessoryRuns`),
     *  written by the measure build and by the editor's measurement refresh. The Orders list pairs a row with the drawing by
     *  these ids and nothing else: never by name, profile or room. Empty for a row someone wrote by hand. */
    run_ids: z.array(z.string().trim().min(1).max(120)).max(500).default([]),
    quantity: z.number().finite().nonnegative().max(1e8).default(0),
    rate: money.nullable().default(null),
    note: text.default(''),
    /**
     * WHICH ACCESSORY THIS IS, as the catalogue names it.
     *
     * Jacky, 19 Sep, opening the real O-00000004: *"wont allow adding trims or able selecting type of
     * underlay or moisture barrier under the product switch like previous platform"*. The seed list used to
     * be one generic line per kind with a free-text colour and no rate, so "choose the underlay" and "add
     * trims" had nowhere to happen. These three fields are the catalogue binding: the kind puts it under the
     * right switch, the product and variant are the TYPE he picked (2mm Green UD, U Channel 12mm, Moisture
     * Barrier), and its cost is the office's own read. Defaulted, so every stored proposal still parses.
     */
    kind: z.string().trim().max(40).default(''),
    product_id: z.string().trim().max(80).nullable().default(null),
    variant_id: z.string().trim().max(160).nullable().default(null),
});
/**
 * The accessory switches the builder offers, in the order they read on screen — the catalogue's own
 * accessory families, not a list somebody maintains. `kindOfAccessory` is the one place a catalogue row
 * becomes one of these.
 */
export const ACCESSORY_KINDS = ['underlay', 'moisture_barrier', 'scotia', 'trims', 'adhesive', 'levelling', 'stair'];
/** What a catalogue row calls itself: its `type`, or its `sub_type` when the type is blank (Adhesive). */
export function kindOfAccessory(type, subType) {
    const t = (type ?? '').trim().toLowerCase();
    const s = (subType ?? '').trim().toLowerCase();
    if (t.startsWith('underlay') || s.startsWith('underlay') || s.includes('backing'))
        return 'underlay';
    if (t.includes('protective') || t.startsWith('moisture') || s.startsWith('moisture'))
        return 'moisture_barrier';
    if (t.startsWith('edging') || t.startsWith('scotia'))
        return 'scotia';
    if (t.startsWith('trim') || s.startsWith('trim'))
        return 'trims';
    if (t.startsWith('adhesive') || s.startsWith('adhesive'))
        return 'adhesive';
    if (t.startsWith('levelling'))
        return 'levelling';
    if (t.startsWith('stair'))
        return 'stair';
    // A placeholder row ("Custom Accessories") is not a switch; the face offers only the known families.
    return 'other';
}
//# sourceMappingURL=proposal-services.js.map